import React, { useState, useEffect } from 'react';
import { ShieldAlert, Plus, Pencil, Check, X, ToggleLeft, ToggleRight } from 'lucide-react';
import { getViolationTypes, createViolationType, updateViolationType, ViolationType } from '../services/api';

const CONSEQUENCE_COLORS: Record<string, string> = {
  Warning:    'bg-yellow-100 text-yellow-800',
  Suspension: 'bg-orange-100 text-orange-800',
  Ban:        'bg-red-100 text-red-800'
};

const emptyForm = {
  name: '',
  description: '',
  healthScoreImpact: -10,
  consequenceType: 'Warning' as 'Warning' | 'Suspension' | 'Ban',
  suspensionDays: null as number | null,
  expiresAfterDays: 180,
};

const ViolationTypesPage: React.FC = () => {
  const [types, setTypes] = useState<ViolationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTypes();
  }, [showInactive]);

  const loadTypes = async () => {
    setLoading(true);
    try {
      const res = await getViolationTypes(showInactive);
      setTypes(res.violationTypes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (vt: ViolationType) => {
    setEditingId(vt.violationTypeId);
    setForm({
      name: vt.name,
      description: vt.description,
      healthScoreImpact: vt.healthScoreImpact,
      consequenceType: vt.consequenceType,
      suspensionDays: vt.suspensionDays,
      expiresAfterDays: vt.expiresAfterDays,
    });
    setShowCreateForm(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        suspensionDays: form.consequenceType === 'Suspension' ? form.suspensionDays : null
      };
      if (editingId) {
        await updateViolationType(editingId, payload);
      } else {
        await createViolationType(payload);
        setShowCreateForm(false);
      }
      setEditingId(null);
      setForm({ ...emptyForm });
      await loadTypes();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (vt: ViolationType) => {
    try {
      await updateViolationType(vt.violationTypeId, { isActive: !vt.isActive });
      await loadTypes();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const FormRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="grid grid-cols-3 gap-4 items-start">
      <label className="text-sm font-medium text-gray-700 pt-2">{label}</label>
      <div className="col-span-2">{children}</div>
    </div>
  );

  const inputClass = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-buggy-blue";

  const ViolationForm = () => (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 space-y-4">
      <h3 className="font-bold text-buggy-dark">{editingId ? 'Edit Violation Type' : 'New Violation Type'}</h3>
      <FormRow label="Name">
        <input className={inputClass} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Leaving Trash" />
      </FormRow>
      <FormRow label="Description">
        <textarea className={inputClass} rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Explain what this violation means..." />
      </FormRow>
      <FormRow label="Consequence">
        <select className={inputClass} value={form.consequenceType} onChange={e => setForm(f => ({ ...f, consequenceType: e.target.value as any }))}>
          <option value="Warning">Warning (no suspension)</option>
          <option value="Suspension">Temporary Suspension</option>
          <option value="Ban">Permanent Ban</option>
        </select>
      </FormRow>
      {form.consequenceType === 'Suspension' && (
        <FormRow label="Suspension Days">
          <input type="number" min={1} max={365} className={inputClass} value={form.suspensionDays || ''} onChange={e => setForm(f => ({ ...f, suspensionDays: parseInt(e.target.value) || null }))} placeholder="e.g. 7" />
        </FormRow>
      )}
      <FormRow label="Health Score Impact">
        <div className="flex items-center gap-3">
          <input type="number" max={-1} className={inputClass} value={form.healthScoreImpact} onChange={e => setForm(f => ({ ...f, healthScoreImpact: parseInt(e.target.value) || -10 }))} placeholder="-20" />
          <span className="text-sm text-gray-500 whitespace-nowrap">Must be negative</span>
        </div>
      </FormRow>
      <FormRow label="Expires After (days)">
        <input type="number" min={1} className={inputClass} value={form.expiresAfterDays} onChange={e => setForm(f => ({ ...f, expiresAfterDays: parseInt(e.target.value) || 180 }))} />
      </FormRow>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-3 pt-2">
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-buggy-blue text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          <Check size={16} /> {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={() => { cancelEdit(); setShowCreateForm(false); }} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">
          <X size={16} /> Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <ShieldAlert size={20} className="text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-buggy-dark">Violation Types</h1>
            <p className="text-sm text-gray-500">Define the behaviors that affect member account health</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" className="rounded" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Show inactive
          </label>
          {!showCreateForm && !editingId && (
            <button onClick={() => { setShowCreateForm(true); setEditingId(null); setForm({ ...emptyForm }); }} className="flex items-center gap-2 px-4 py-2 bg-buggy-blue text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              <Plus size={16} /> Add Violation Type
            </button>
          )}
        </div>
      </div>

      {showCreateForm && !editingId && <div className="mb-6"><ViolationForm /></div>}

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : (
        <div className="space-y-3">
          {types.map(vt => (
            <div key={vt.violationTypeId}>
              {editingId === vt.violationTypeId ? (
                <ViolationForm />
              ) : (
                <div className={`bg-white rounded-xl border p-5 ${!vt.isActive ? 'opacity-50' : ''}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-bold text-buggy-dark">{vt.name}</h3>
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CONSEQUENCE_COLORS[vt.consequenceType]}`}>
                          {vt.consequenceType}{vt.suspensionDays ? ` (${vt.suspensionDays}d)` : ''}
                        </span>
                        {!vt.isActive && <span className="text-xs text-gray-400 italic">Inactive</span>}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{vt.description}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Score impact: <span className="font-semibold text-red-600">{vt.healthScoreImpact}</span></span>
                        <span>Expires after: <span className="font-semibold">{vt.expiresAfterDays} days</span></span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => startEdit(vt)} className="p-2 text-gray-400 hover:text-buggy-blue hover:bg-blue-50 rounded-lg transition">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => handleToggleActive(vt)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition" title={vt.isActive ? 'Deactivate' : 'Activate'}>
                        {vt.isActive ? <ToggleRight size={20} className="text-green-500" /> : <ToggleLeft size={20} />}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          {types.length === 0 && (
            <div className="text-center py-12 text-gray-400">No violation types defined yet.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViolationTypesPage;
